import { Link, useParams, Navigate } from "react-router-dom";
import Reveal from "@/components/Reveal";
import CountUp from "@/components/CountUp";
import { getCase, cases } from "@/data/cases";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";

export const CaseStudy = () => {
  const { slug } = useParams();
  const data = getCase(slug);

  useSeo({
    title: data ? `${data.title} · ${data.sector}` : "Case study",
    description: data ? `${data.brief}`.slice(0, 152) : undefined,
    path: data ? `/work/${data.slug}` : "/work",
    image: data ? data.image : undefined,
    type: "article",
    jsonLd: data
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              headline: data.title,
              description: data.brief,
              image: data.image,
              author: { "@id": `${SITE_URL}#organization` },
              publisher: { "@id": `${SITE_URL}#organization` },
              datePublished: `${data.year}-01-01`,
              about: data.sector,
              mainEntityOfPage: `${SITE_URL}/work/${data.slug}`,
            },
            breadcrumb([
              { name: "Home", path: "/" },
              { name: "Work", path: "/work" },
              { name: data.title, path: `/work/${data.slug}` },
            ]),
          ],
        }
      : null,
    jsonLdId: "case-jsonld",
  });

  if (!data) return <Navigate to="/work" replace />;

  const idx = cases.findIndex((c) => c.slug === slug);
  const prev = cases[(idx - 1 + cases.length) % cases.length];
  const next = cases[(idx + 1) % cases.length];

  return (
    <article data-testid="case-study-page">
      <header className="case-head wrap">
        <Reveal>
          <div className="crumb">
            <span>Case</span>
            <span>·</span>
            <span>{data.sector}</span>
            <span>·</span>
            <span>{data.year}</span>
          </div>
          <h1 data-testid="case-title">{data.title}</h1>
        </Reveal>
      </header>

      <div className="wrap">
        <Reveal className="case-hero">
          <img src={data.image} alt={`${data.title} — Asia Sourcing case study`} width="1600" height="900" loading="eager" fetchpriority="high" />
        </Reveal>

        <div className="case-body">
          <div>
            <Reveal className="block">
              <p className="eyebrow">Brief</p>
              <p>{data.brief}</p>
            </Reveal>
            <Reveal className="block">
              <p className="eyebrow">Approach</p>
              <p>{data.approach}</p>
            </Reveal>
            <Reveal className="block">
              <p className="eyebrow">Outcome</p>
              <p>{data.outcome}</p>
            </Reveal>
          </div>
          <aside className="case-meta">
            <Reveal as="dl">
              <dt>Client</dt><dd>{data.client}</dd>
              <dt>Sector</dt><dd>{data.sector}</dd>
              <dt>SKUs</dt><dd>{data.sku}</dd>
              <dt>Units</dt><dd>{data.units}</dd>
              <dt>Lead time</dt><dd>{data.leadTime}</dd>
            </Reveal>
          </aside>
        </div>

        <div className="case-stats">
          {data.stats.map((s, i) => (
            <Reveal key={i} className="stat">
              <div className="num">
                <CountUp target={s.num} suffix={s.suffix || ""} testId={`case-stat-${i}`} />
              </div>
              <div className="lbl">{s.lbl}</div>
            </Reveal>
          ))}
        </div>

        <section className="cta">
          <Reveal as="p" className="eyebrow">Start an enquiry</Reveal>
          <Reveal as="h2" className="display">Your order, documented.</Reveal>
          <Reveal className="row">
            <Link to="/contact" className="btn btn-fill btn-lg" data-testid="case-cta">
              Send an enquiry
            </Link>
          </Reveal>
        </section>

        <nav
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "1rem",
            padding: "2rem 0 4rem",
            borderTop: "1px solid var(--line)",
            fontFamily: "var(--mono)",
            fontSize: ".74rem",
            letterSpacing: ".08em",
            textTransform: "uppercase",
          }}
          aria-label="Case study pagination"
          data-testid="case-pagination"
        >
          <Link to={`/work/${prev.slug}`} className="btn btn-ghost">← {prev.title}</Link>
          <Link to={`/work/${next.slug}`} className="btn btn-ghost">{next.title} →</Link>
        </nav>
      </div>
    </article>
  );
};

export default CaseStudy;
